import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { schoolsDB } from '../../lib/db/schools';
import { getTerminologyTerm } from '../../lib/terminology';
import Modal from '../common/Modal';
import { getBeltRowClasses, getDrillColorLevel, getColorLevelSortIndex, getDrillStage, getStagesForTrack } from '../../lib/beltLevelColors';
import { getBeltsForTrack, getDrillIdsForTrack, normalizeTrack, getWhiteBeltLevelForTrack } from '../../lib/track';

export default function DrillSelector({ 
  schoolId, 
  schoolData,
  userBelt,
  track: userTrack = 'adult',
  onSelect,
  // Multi-select props
  multiSelectMode = false,
  customLists = [],
  selectedDrills = [],
  editingListId = null,
  onToggleDrill = null,
  onLoadList = null,
  onSaveList = null,
  onDeleteList = null,
  onClearSelections = null,
  onReorderDrills = null
}) {
  const [drills, setDrills] = useState([]);
  const [belts, setBelts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStage, setSelectedStage] = useState('all');
  const [confirmState, setConfirmState] = useState({ isOpen: false, drill: null });
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, listId: null, listName: '' });
  const [selectionMode, setSelectionMode] = useState('single'); // 'single' | 'custom-list'
  const [editingListName, setEditingListName] = useState(''); // Track which list we're editing
  const { t, i18n } = useTranslation();
  const track = normalizeTrack(userTrack);
  const trackBelts = getBeltsForTrack(belts, track);
  const trackDrillIds = getDrillIdsForTrack(belts, track);
  const trackDrills = drills.filter((drill) => trackDrillIds.has(drill.id));
  const pickerConfig = schoolData?.drillPickerConfig;
  const stages = getStagesForTrack(track, pickerConfig);

  // Auto-switch to custom-list mode when editingListId is set
  useEffect(() => {
    if (editingListId) {
      setSelectionMode('custom-list');
      // Find list name
      const list = customLists.find(l => l.listId === editingListId);
      setEditingListName(list?.name || '');
    }
  }, [editingListId, customLists]);

  useEffect(() => {
    setSelectedStage('all');
    setSelectedCategory('all');
  }, [track]);
  
  const handleConfirm = () => {
    if (confirmState.drill) {
      onSelect(confirmState.drill);
    }
    setConfirmState({ isOpen: false, drill: null });
  };
  
  const handleCancelConfirm = () => {
    setConfirmState({ isOpen: false, drill: null });
  };
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [drillsData, beltsData] = await Promise.all([
          schoolsDB.getAllDrills(schoolId),
          schoolsDB.getBelts(schoolId)
        ]);
        
        setDrills(drillsData);
        setBelts(beltsData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, [schoolId]);
  
  const getUserBeltLevel = () => {
    if (!belts.length || !userBelt) return null;
    const belt = belts.find((b) => b.id === userBelt);
    // Belt missing, or from the other track (e.g. switched adult/youth in
    // Settings without a matching belt yet) — fall back to that track's
    // white belt rather than misreporting the mismatched belt's level.
    if (!belt) return getWhiteBeltLevelForTrack(track);
    if (track === 'youth' && belt.level >= 0) return getWhiteBeltLevelForTrack(track);
    if (track === 'adult' && belt.level < 0) return getWhiteBeltLevelForTrack(track);
    return belt.level;
  };

  const getMinBeltLevelForDrill = (drillId) => {
    const matching = trackBelts.filter((b) => b.drillIds.includes(drillId));
    if (matching.length === 0) return null;
    return Math.min(...matching.map((b) => b.level));
  };

  const isAboveBelt = (drill) => {
    const userLevel = getUserBeltLevel();
    if (userLevel == null) return false;
    const minLevel = getMinBeltLevelForDrill(drill.id);
    if (minLevel == null) return false;
    return minLevel > userLevel;
  };

  const getBeltForDrill = (drillId) => {
    const matchingBelts = trackBelts.filter((belt) => belt.drillIds.includes(drillId));
    if (matchingBelts.length === 0) return null;
    return matchingBelts.reduce((min, belt) =>
      belt.level < min.level ? belt : min
    );
  };

  const filteredDrills = trackDrills.filter((drill) => {
    const drillName = drill[`name${i18n.language === 'he' ? 'He' : 'En'}`].toLowerCase();
    const matchesSearch = drillName.includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || drill.category.toLowerCase() === selectedCategory.toLowerCase();
    const colorLevel = getDrillColorLevel(drill, trackBelts, track, pickerConfig);
    const drillStage = getDrillStage(colorLevel, track, pickerConfig);
    const matchesStage = selectedStage === 'all' || drillStage === selectedStage;
    const hiddenByBelt = schoolData?.settings?.hideUnavailableDrills && isAboveBelt(drill);
    return matchesSearch && matchesCategory && matchesStage && !hiddenByBelt;
  });

  // Sort drills: selected first (custom-list), then by rank color, then drill order
  const sortedDrills = [...filteredDrills].sort((a, b) => {
    if (selectionMode === 'custom-list') {
      const aSelected = selectedDrills.includes(a.id);
      const bSelected = selectedDrills.includes(b.id);
      
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      
      if (aSelected && bSelected) {
        const aIndex = selectedDrills.indexOf(a.id);
        const bIndex = selectedDrills.indexOf(b.id);
        return aIndex - bIndex;
      }
    }

    const aColorSort = getColorLevelSortIndex(getDrillColorLevel(a, trackBelts, track, pickerConfig), track, pickerConfig);
    const bColorSort = getColorLevelSortIndex(getDrillColorLevel(b, trackBelts, track, pickerConfig), track, pickerConfig);
    if (aColorSort !== bColorSort) return aColorSort - bColorSort;

    return a.order - b.order;
  });

  // Get unique categories for filter
  const categories = ['all', ...new Set(trackDrills.map((d) => d.category))];
  
  const handleSelect = (drill) => {
    if (selectionMode === 'single') {
      // Single select mode - navigate directly to timer
      // Skip above-belt confirmation when school hides unavailable drills
      if (isAboveBelt(drill) && !schoolData?.settings?.hideUnavailableDrills) {
        setConfirmState({ isOpen: true, drill });
        return;
      }
      onSelect(drill);
      return;
    }
    
    // Custom list mode - toggle drill selection
    if (onToggleDrill) {
      onToggleDrill(drill.id);
    }
  };
  
  const handleToggleDrill = (drillId) => {
    if (onToggleDrill) {
      onToggleDrill(drillId);
    }
  };
  
  const handleSaveAndStart = async () => {
    if (selectedDrills.length < 2) return;
    
    if (editingListId) {
      // Update existing list
      if (onSaveList) {
        await onSaveList(editingListId, selectedDrills);
      }
    } else {
      // New list - prompt for name
      const listName = prompt(t('drill_selector.list_name_prompt'));
      if (!listName || !listName.trim()) return;
      
      if (onSaveList) {
        await onSaveList(null, selectedDrills, listName.trim());
      }
    }
  };

  const handleDeleteFromEditMode = () => {
    if (!editingListId) return;
    
    // Find the list name
    const listToDelete = customLists.find(list => list.listId === editingListId);
    if (listToDelete) {
      setDeleteDialog({
        isOpen: true,
        listId: editingListId,
        listName: listToDelete.name || editingListName
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.listId || !onDeleteList) return;
    
    await onDeleteList(deleteDialog.listId);
    
    // Close dialog
    setDeleteDialog({ isOpen: false, listId: null, listName: '' });
    
    // Return to single drill mode
    setSelectionMode('single');
    if (onClearSelections) {
      onClearSelections();
    }
    setEditingListName('');
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ isOpen: false, listId: null, listName: '' });
  };

  // Toggle functions
  const handleCreateNewList = () => {
    setSelectionMode('custom-list');
    if (onClearSelections) {
      onClearSelections();
    }
    setEditingListName('');
  };

  const handleEditList = async (listId, drillIds) => {
    setSelectionMode('custom-list');
    if (onLoadList) {
      await onLoadList(listId);
    }
    // Find list name
    const list = customLists.find(l => l.listId === listId);
    setEditingListName(list?.name || '');
  };

  const handleSwitchToSingle = () => {
    setSelectionMode('single');
    if (onClearSelections) {
      onClearSelections();
    }
    setEditingListName('');
  };

  // Drag and drop handler for react-beautiful-dnd
  const handleDragEnd = (result) => {
    if (!result.destination || !onReorderDrills) return;
    
    const newOrder = Array.from(selectedDrills);
    const [removed] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, removed);
    
    onReorderDrills(newOrder);
  };
  
  if (loading) return <div>{t('common.loading')}</div>;
  
  if (drills.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>{t('drill_selector.no_drills')}</p>
        <p className="text-sm mt-2">{t('drill_selector.contact_admin')}</p>
      </div>
    );
  }
  
  // Show save button when in custom-list mode with 2+ drills selected
  const showSaveButton = selectionMode === 'custom-list' && selectedDrills.length >= 2;

  return (
    <div className="space-y-3 md:space-y-4">
      {/* Custom Lists Chips - Horizontal scrollable - Always show */}
      <div className="overflow-x-auto pb-2" style={{ direction: 'rtl' }}>
        <div className="flex gap-2 md:gap-3" style={{ direction: 'rtl' }}>
          {/* Toggle chip - shows different text based on mode */}
          {selectionMode === 'single' ? (
            <button
              onClick={handleCreateNewList}
              className="px-4 md:px-6 py-2 md:py-3 rounded-full font-semibold text-sm md:text-base whitespace-nowrap transition-all flex-shrink-0 bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-gray-300"
            >
              {t('custom_lists.new_list')}
            </button>
          ) : (
            <button
              onClick={handleSwitchToSingle}
              className="px-4 md:px-6 py-2 md:py-3 rounded-full font-semibold text-sm md:text-base whitespace-nowrap transition-all flex-shrink-0 bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-gray-300"
            >
              {t('drill_selector.select_single_drill')}
            </button>
          )}
          
          {/* Existing custom lists */}
          {customLists.map(list => (
            <button
              key={list.listId}
              onClick={() => handleEditList(list.listId, list.drillIds)}
              className={`px-4 md:px-6 py-2 md:py-3 rounded-full font-semibold text-sm md:text-base whitespace-nowrap transition-all flex-shrink-0 ${
                editingListId === list.listId
                  ? 'bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-md'
                  : 'bg-white border-2 border-primary-300 text-primary-700 hover:bg-primary-50'
              }`}
            >
              {list.name}
            </button>
          ))}
        </div>
      </div>

      {/* Action Buttons - Positioned below chips row, above search bar */}
      {showSaveButton && (
        <div className="flex gap-2 w-full">
          {/* Delete Button - Only show when editing existing list */}
          {editingListId && editingListName !== '' && (
            <button
              onClick={handleDeleteFromEditMode}
              className="flex-1 px-6 md:px-10 py-4 md:py-6 bg-gradient-to-r from-red-600 to-red-500 text-white rounded-2xl font-bold hover:shadow-xl text-lg md:text-2xl min-h-[52px] md:min-h-[64px] flex items-center justify-center shadow-lg hover:scale-105 active:scale-95 transition-all"
            >
              🗑️ {t('drill_selector.delete_list') || 'מחק רשימה'}
            </button>
          )}
          
          {/* Save/Update Button */}
          <button
            onClick={handleSaveAndStart}
            className={`${editingListId && editingListName !== '' ? 'flex-1' : 'w-full'} px-6 md:px-10 py-4 md:py-6 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-2xl font-bold hover:shadow-xl text-lg md:text-2xl min-h-[52px] md:min-h-[64px] flex items-center justify-center shadow-lg hover:scale-105 active:scale-95 transition-all`}
          >
            💾 {editingListId 
              ? t('drill_selector.update_list_and_start')
              : t('drill_selector.save_list_and_start')
            }
          </button>
        </div>
      )}
      
      {/* Search and Filter Controls */}
      <div className="space-y-2 md:space-y-4">
        <div>
          <input
            type="text"
            placeholder={t('drill_selector.search_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg text-sm md:text-lg min-h-[40px]"
            dir={i18n.language === 'he' ? 'rtl' : 'ltr'}
          />
        </div>
        
        <div>
          <select
            value={selectedStage}
            onChange={(e) => setSelectedStage(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            className="w-full px-3 py-2 border rounded-lg text-sm md:text-lg min-h-[40px] bg-white"
            dir={i18n.language === 'he' ? 'rtl' : 'ltr'}
          >
            <option value="all">{t('drill_selector.all_stages')}</option>
            {stages.map((stage) => (
              <option key={stage} value={stage}>
                {t('drill_selector.stage', { number: stage })}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-2.5 md:px-4 py-1.5 rounded-lg text-xs md:text-sm font-medium transition-colors min-h-[36px] ${
                selectedCategory === category
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {category === 'all' ? t('drill_selector.all_categories') : t(`categories.${category.toLowerCase()}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Drill List - Compact design: 60px height, single line format */}
      {sortedDrills.length === 0 ? (
        <div className="text-center py-6 text-gray-500 text-sm md:text-lg">
          <p>{t('drill_selector.no_results')}</p>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="drills">
            {(provided) => (
              <div 
                {...provided.droppableProps} 
                ref={provided.innerRef} 
                className="space-y-1.5 md:space-y-2 no-drag-transition"
              >
                {sortedDrills.map((drill, index) => {
                  const drillBelt = getBeltForDrill(drill.id);
                  const beltLevel = getDrillColorLevel(drill, trackBelts, track, pickerConfig);
                  const drillName = drill[`name${i18n.language === 'he' ? 'He' : 'En'}`];
                  const beltName = drillBelt?.[`name${i18n.language === 'he' ? 'He' : 'En'}`];
                  const isSelected = selectionMode === 'custom-list' && selectedDrills.includes(drill.id);
                  const isDraggable = selectionMode === 'custom-list' && selectedDrills.length >= 2 && isSelected;
                  const rowClasses = getBeltRowClasses(beltLevel, {
                    selected: selectionMode === 'custom-list' && isSelected,
                    track: track,
                    config: pickerConfig,
                  });
                  
                  // Only make selected drills draggable
                  if (!isDraggable) {
                    return (
                      <div
                        key={drill.id}
                        className={`w-full px-4 py-2 border rounded-lg transition-all duration-200
                                  flex items-center justify-between min-h-[56px] max-h-[60px] ${rowClasses}`}
                      >
                        {selectionMode === 'custom-list' && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleDrill(drill.id)}
                            className="h-5 w-5 accent-primary-500 cursor-pointer ml-3 md:ml-4"
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        {selectionMode === 'single' && (
                          <div className="ml-3 md:ml-4 text-gray-400 flex-shrink-0">▶</div>
                        )}
                        <button
                          onClick={() => handleSelect(drill)}
                          className="flex-1 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2">
                            {drill.video && (
                              <span className="text-base flex-shrink-0" title="Video available">📹</span>
                            )}
                            <div className="flex-1 text-right text-sm md:text-base font-medium text-gray-800">
                              {drillName}
                            </div>
                          </div>
                          <div className="text-xs md:text-sm text-gray-600">
                            {beltName} • {t(`categories.${drill.category.toLowerCase()}`)}
                          </div>
                        </button>
                      </div>
                    );
                  }
                  
                  // Draggable drill item
                  const selectedIndex = selectedDrills.indexOf(drill.id);
                  return (
                    <Draggable 
                      key={drill.id} 
                      draggableId={drill.id} 
                      index={selectedIndex}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            ...provided.draggableProps.style,
                            transition: 'none',
                          }}
                          className={`w-full px-4 py-2 border rounded-lg
                                    flex items-center justify-between min-h-[56px] max-h-[60px] ${getBeltRowClasses(beltLevel, {
                            selected: !snapshot.isDragging,
                            dragging: snapshot.isDragging,
                            track: track,
                            config: pickerConfig,
                          })}`}
                        >
                          <div 
                            {...provided.dragHandleProps}
                            className="ml-3 md:ml-4 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing select-none flex-shrink-0"
                            style={{ fontSize: '18px', lineHeight: '1', userSelect: 'none' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            ⠿
                          </div>
                          {selectionMode === 'custom-list' && (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleDrill(drill.id)}
                              className="h-5 w-5 accent-primary-500 cursor-pointer ml-2 md:ml-3"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                          <button
                            onClick={() => handleSelect(drill)}
                            className="flex-1 flex items-center justify-between"
                          >
                            <div className="flex items-center gap-2">
                              {drill.video && (
                                <span className="text-base flex-shrink-0" title="Video available">📹</span>
                              )}
                              <div className="flex-1 text-right text-sm md:text-base font-medium text-gray-800">
                                {drillName}
                              </div>
                            </div>
                            <div className="text-xs md:text-sm text-gray-600">
                              {beltName} • {t(`categories.${drill.category.toLowerCase()}`)}
                            </div>
                          </button>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
      
      {/* Hint text when in custom-list mode with less than 2 drills */}
      {selectionMode === 'custom-list' && selectedDrills.length < 2 && (
        <div className="text-center text-sm md:text-base text-gray-500 py-2">
          {t('drill_selector.select_multiple_drills')}
        </div>
      )}
      
      {/* Confirmation Modal */}
      {confirmState.isOpen && confirmState.drill && (
        <Modal
          isOpen={confirmState.isOpen}
          title={t('drill_selector.above_belt_title') || 'Warning'}
          message={
            t('drill_selector.above_belt_warning', { 
              drillTerm: (() => {
                const term = getTerminologyTerm(schoolData, 'drill', i18n.language) || (i18n.language === 'he' ? 'תרגיל' : 'drill');
                return i18n.language === 'he' ? term : term.toLowerCase();
              })(),
              beltTerm: (() => {
                const term = getTerminologyTerm(schoolData, 'belt', i18n.language) || (i18n.language === 'he' ? 'חגורה' : 'belt');
                return i18n.language === 'he' ? term : term.toLowerCase();
              })(),
              drill: confirmState.drill[`name${i18n.language === 'he' ? 'He' : 'En'}`],
              belt: getBeltForDrill(confirmState.drill.id)[`name${i18n.language === 'he' ? 'He' : 'En'}`]
            }) + '\n\n' + t('drill_selector.above_belt_confirm')
          }
          confirmLabel={t('common.yes') || 'Yes'}
          cancelLabel={t('common.no') || 'No'}
          onConfirm={handleConfirm}
          onCancel={handleCancelConfirm}
          type="confirm"
          variant="warning"
        />
      )}
      
      {/* Delete List Confirmation Modal */}
      {deleteDialog.isOpen && (
        <Modal
          isOpen={deleteDialog.isOpen}
          title={t('drill_selector.delete_list_confirm_title') || 'מחק רשימה?'}
          message={
            (t('drill_selector.delete_list_confirm_message', { name: deleteDialog.listName }) || 
             `האם למחוק את "${deleteDialog.listName}"?`) +
            '\n\n' +
            (t('drill_selector.delete_list_warning') || 'פעולה זו לא ניתנת לביטול')
          }
          confirmLabel={t('drill_selector.delete') || '🗑️ מחק'}
          cancelLabel={t('common.cancel') || 'ביטול'}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          type="confirm"
          variant="danger"
        />
      )}
    </div>
  );
}
